import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { PILLARS, WHAT_WE_DO } from '@/lib/content';
import SectionHeading from '@/components/sections/SectionHeading';
import SEO from '@/components/SEO';

export default function Services() {
    return (
        <div className="pt-32 bg-white">
            <SEO title="Services" description="Three pillars and six capabilities: brand building, broadcast & CTV media, social, and full-funnel direct response." path="/services" />
            <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-16">
                <div className="text-xs uppercase tracking-[0.35em] font-bold text-brand-orange mb-5">
                    Services
                </div>
                <h1 className="font-display font-black text-brand-navy text-5xl md:text-7xl lg:text-8xl tracking-tight leading-[0.95] max-w-5xl">
                    Built for response.<br />
                    <span className="italic font-medium text-brand-orange">Tuned for results.</span>
                </h1>
                <p className="text-brand-navy/70 text-lg max-w-2xl mt-8 leading-relaxed">
                    Three pillars and six capabilities — orchestrated end-to-end so your media spend
                    works harder, faster, and louder than the dealership down the street.
                </p>
            </section>

            <section className="bg-brand-navy text-white">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-14 md:py-18 lg:py-20">
                    <SectionHeading
                        eyebrow="Three Pillars"
                        title="The full direct response stack."
                        description="Brand. Broadcast. Social. Each pillar engineered to compound the next."
                        onDark
                    />
                    <div className="grid md:grid-cols-3 mt-10 lg:mt-12 border-t border-white/15">
                        {PILLARS.map((p, idx) => (
                            <article
                                key={p.key}
                                className={`group relative p-7 md:p-8 lg:p-9 ${
                                    idx !== 2 ? 'md:border-r border-white/15' : ''
                                } border-b md:border-b-0 border-white/15 hover:bg-white/[0.04] transition-colors`}
                            >
                                <div className="flex items-baseline justify-between mb-6">
                                    <span className="font-mono text-xs uppercase tracking-[0.3em] text-brand-orange font-bold">
                                        Pillar {p.number}
                                    </span>
                                    <ArrowUpRight className="w-5 h-5 text-white/30 group-hover:text-brand-orange transition-colors" />
                                </div>
                                <h3 className="font-display font-extrabold text-2xl md:text-3xl text-white leading-tight tracking-tight mb-4">
                                    {p.title}
                                </h3>
                                <p className="text-white/70 text-[15px] leading-relaxed">{p.body}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-brand-cream py-14 md:py-18 lg:py-20">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                    <SectionHeading
                        eyebrow="What we do for you"
                        title="Six capabilities. One accountable team."
                        description="Whether you’re launching a model year, recovering from a publicity crisis, or expanding into new markets — we have the right play."
                    />
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 mt-10 lg:mt-12 border-t border-l border-brand-navy/15">
                        {WHAT_WE_DO.map((item, idx) => (
                            <div
                                key={item.title}
                                className="border-b border-r border-brand-navy/15 p-7 md:p-8 lg:p-9 bg-white group hover:bg-brand-navy transition-colors duration-300"
                            >
                                <div className="text-xs font-mono uppercase tracking-[0.3em] text-brand-orange mb-5 font-bold">
                                    {String(idx + 1).padStart(2, '0')} / 06
                                </div>
                                <h3 className="font-display font-extrabold text-2xl md:text-3xl text-brand-navy group-hover:text-white leading-tight mb-4 transition-colors">
                                    {item.title}
                                </h3>
                                <p className="text-brand-navy/70 group-hover:text-white/85 text-[15px] leading-relaxed transition-colors">
                                    {item.body}
                                </p>
                            </div>
                        ))}
                    </div>

                    <div className="mt-16 text-center">
                        <Link
                            to="/contact"
                            className="inline-flex items-center gap-3 bg-brand-orange hover:bg-brand-navy text-white px-8 py-4 text-xs font-bold uppercase tracking-[0.25em] transition-colors"
                        >
                            Brief us on your next campaign <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
